import React, { useEffect, useRef, useState } from "react";
import Select from "../select";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { useAlreadyExists } from "../confirmDialog";

const API = process.env.REACT_APP_API_ROOT;

/**
 * The tag picker, with a way out of the dead end.
 *
 * Typing a tag that does not exist used to show "No options" — accurate and
 * useless, because the only way forward was to leave the form, go to the Tags
 * page, create it, come back, and start again. It now offers to create it.
 *
 * A tag is not just a name though: it belongs to genres, and that is what makes
 * it useful for grouping later. So creating one from here asks the same
 * question the Tags page asks, rather than quietly making an orphan.
 *
 * `onCreated` lets the parent refresh its own option list, so the new tag is
 * there for the next post without a reload.
 *
 * Selecting and creating are SEPARATE (28 Oct 2026). The field picks existing
 * tags only; a new tag is made with the "Create new tag" button and its own
 * name box. It used to be a creatable select, where whatever sat in the search
 * box — sometimes two searches run together ("HabitsFocus") — could be turned
 * into a tag by Enter, and it was hard to tell a picked tag from a typed one.
 * The picked tags are listed under the field with their ids.
 */
const TagSelect = ({ value, onChange, options = [], adminInfo, onCreated, placeholder = "Search existing tags..." }) => {
    // The create dialog: open or not, and the name in its box.
    const [dialogOpen, setDialogOpen] = useState(false);
    const [pendingName, setPendingName] = useState("");
    // What is typed in the search, to start the dialog's name box with.
    const [searchText, setSearchText] = useState("");
    const showAlreadyExists = useAlreadyExists();
    const [genreOptions, setGenreOptions] = useState([]);
    // "loading" and "failed" are different things to say, and an empty list is
    // a third. Without this the field claimed to be loading forever whenever
    // the fetch failed.
    const [genreState, setGenreState] = useState("idle");
    const [chosenGenres, setChosenGenres] = useState([]);
    const [primaryGenreId, setPrimaryGenreId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const genreField = useRef(null);

    // Only fetched when the dialog is actually opened — most sessions never
    // create a tag, and this is a list of every genre.
    useEffect(() => {
        // Only "idle" fetches. `genreState` is a dependency, so allowing
        // "failed" through here would re-run the effect the moment the failure
        // was recorded and retry forever against an endpoint that is down.
        // Closing the dialog resets a failure to idle, so reopening retries
        // once, deliberately.
        if (!dialogOpen || !adminInfo?.token) return;
        if (genreState !== "idle") return;

        (async () => {
            setGenreState("loading");
            try {
                const headers = { Authorization: `Bearer ${adminInfo.token}` };
                const { data: response } = await axios.get(`${API}/api/admin/genre/getall`, { headers });
                setGenreOptions(
                    (response?.data?.allGenres || [])
                        .map((g) => ({
                            value: g._id,
                            label: g.translations?.find((t) => t.langCode === "en")?.name || "(unnamed)",
                        }))
                        .filter((o) => o.value)
                );
                setGenreState("ready");
            } catch (error) {
                console.error("TagSelect fetchGenres catch error::", error);
                setGenreState("failed");
                toast.error("Could not load genres.");
            }
        })();
    }, [dialogOpen, adminInfo, genreState]);

    // Focus the name when it needs typing, else the genre — the next thing to
    // say. On the frame after, not on mount: react-modal moves focus to the
    // dialog itself once it has rendered, and would take it straight back.
    const nameField = useRef(null);
    useEffect(() => {
        if (!dialogOpen) return undefined;
        const frame = requestAnimationFrame(() => (pendingName ? genreField.current : nameField.current)?.focus());
        return () => cancelAnimationFrame(frame);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dialogOpen]);

    const openDialog = () => {
        setPendingName(searchText.trim());
        setDialogOpen(true);
    };

    // Same rule as the server: names are unique ignoring case.
    const existingWithName = (name) =>
        options.find((o) => (o.label || "").trim().toLowerCase() === name.trim().toLowerCase());

    const closeDialog = () => {
        // Arm one retry for the next time the dialog opens.
        if (genreState === "failed") setGenreState("idle");
        setDialogOpen(false);
        setPendingName("");
        setChosenGenres([]);
        setPrimaryGenreId(null);
    };

    // Enter creates the tag, so the dialog can be finished from the keyboard
    // that opened it — the tag name was typed, not clicked, and reaching for
    // the mouse only to confirm is a break in the middle of one action.
    //
    // A form rather than a key handler on purpose: react-select swallows Enter
    // whenever its own menu is open, so picking a genre from the list cannot
    // also submit. That judgement belongs to the field with the open menu, not
    // to us guessing at its state.
    const handleSubmit = (event) => {
        event.preventDefault();
        if (isSaving) return;
        handleCreate();
    };

    const handleCreate = async () => {
        const name = (pendingName || "").trim();
        if (!name) {
            toast.error("Type the new tag's name.");
            return;
        }
        const existing = existingWithName(name);
        if (existing) {
            // Already a tag: pick it instead of making a clash.
            if (!(value || []).some((v) => v.value === existing.value)) onChange([...(value || []), existing]);
            toast.info(`"${existing.label}" already exists — it is now selected.`);
            closeDialog();
            return;
        }

        setIsSaving(true);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.post(
                `${API}/api/admin/tag/add`,
                {
                    name,
                    genres: chosenGenres.map((g) => ({
                        genreId: g.value,
                        genreName: g.label,
                        // Falls back to the first genre server-side if none is
                        // marked, so this cannot produce a tag with no primary.
                        isPrimary: g.value === primaryGenreId,
                    })),
                },
                { headers }
            );

            if (!response.status) {
                toast.error(response.message || `Could not create "${name}".`);
                return;
            }

            // The add endpoint returns a message, not the tag, so the fresh list
            // is where the new id comes from.
            const refreshed = (await onCreated?.()) || [];
            const created = refreshed.find(
                (o) => (o.label || "").toLowerCase() === name.toLowerCase()
            );
            if (created) {
                onChange([...(value || []), created]);
                toast.success(`Tag "${name}" created.`);
            } else {
                // Created server-side but not found in the refreshed list —
                // say so rather than reporting a clean success for a tag that
                // is not actually selected.
                toast.success(`Tag "${name}" created. Search for it to attach it.`);
            }
            closeDialog();
        } catch (error) {
            console.error("TagSelect create catch error::", error);
            if (!showAlreadyExists(error)) toast.error(error.response?.data?.error || error.response?.data?.message || "Could not create this tag.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Select
                isMulti
                placeholder={placeholder}
                value={value || []}
                onChange={(selected) => onChange(selected || [])}
                options={options}
                inputValue={searchText}
                // Cleared on select and on blur, so one search never runs into
                // the next; kept while typing.
                onInputChange={(input, { action }) => {
                    if (action === "input-change") setSearchText(input);
                    else if (action === "set-value" || action === "input-blur" || action === "menu-close") setSearchText("");
                }}
                noOptionsMessage={({ inputValue }) =>
                    inputValue ? `No tag called "${inputValue}" — use Create new tag below` : "No tags"}
            />
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mt-2">
                <div data-testid="selected-tag-ids" style={{ fontSize: 12, color: "var(--ps-text-muted)" }}>
                    {(value || []).length === 0
                        ? "No tags selected."
                        : (value || []).map((tag) => (
                            <span key={tag.value} className="me-3 d-inline-block" data-tag-id={tag.value}>
                                {tag.label} <code style={{ fontSize: 11 }}>{tag.value}</code>
                            </span>
                        ))}
                </div>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={openDialog}>
                    + Create new tag
                </button>
            </div>

            <Modal
                isOpen={dialogOpen}
                onRequestClose={isSaving ? undefined : closeDialog}
                className="modal_body_sm modal_body_nested"
                overlayClassName="modal_overlay modal_overlay_nested"
            >
                <form className="box_wrapper" onSubmit={handleSubmit}>
                    <h4 className="fw-bold mb-3">New tag</h4>
                    <div className="mb-3">
                        <label className="label">Name:</label>
                        <input
                            ref={nameField}
                            className="form_control w-100"
                            type="text"
                            value={pendingName}
                            onChange={(e) => setPendingName(e.target.value)}
                            placeholder="The new tag's name"
                        />
                        {pendingName.trim() && existingWithName(pendingName) && (
                            <small style={{ color: "#F5A524" }}>
                                "{existingWithName(pendingName).label}" already exists — Create will select it instead.
                            </small>
                        )}
                    </div>
                    <p className="text-muted">Which genres does it belong to?</p>

                    <div className="mb-3">
                        <label className="label">Genres:</label>
                        <Select
                            ref={genreField}
                            isMulti
                            isSearchable
                            placeholder="Search genres..."
                            noOptionsMessage={({ inputValue }) => {
                                if (inputValue) return `No genre matches "${inputValue}"`;
                                if (genreState === "loading") return "Loading genres...";
                                if (genreState === "failed") return "Could not load genres — close and reopen to retry";
                                return "No genres exist yet";
                            }}
                            value={chosenGenres}
                            onChange={(selected) => {
                                const next = selected || [];
                                setChosenGenres(next);
                                // Keep the primary pointing at something that is
                                // still selected.
                                if (!next.some((g) => g.value === primaryGenreId)) {
                                    setPrimaryGenreId(next[0]?.value ?? null);
                                }
                            }}
                            options={genreOptions}
                        />
                        <small className="text-muted">
                            A tag can belong to several genres, or none.
                        </small>
                    </div>

                    {chosenGenres.length > 1 && (
                        <div className="mb-3">
                            <label className="label">Primary genre:</label>
                            <Select
                                isSearchable
                                placeholder="Pick the main one..."
                                value={chosenGenres.find((g) => g.value === primaryGenreId) || null}
                                onChange={(selected) => setPrimaryGenreId(selected?.value ?? null)}
                                options={chosenGenres}
                            />
                            <small className="text-muted">
                                The one this tag sits under when it has to be filed in a single place.
                            </small>
                        </div>
                    )}

                    <div className="d-flex gap-2 justify-content-end mt-3">
                        <button type="button" className="btn btn-outline-secondary" onClick={closeDialog} disabled={isSaving}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-dark" disabled={isSaving}>
                            {isSaving ? "Creating ..." : "Create tag"}
                        </button>
                    </div>
                </form>
            </Modal>
        </>
    );
};

export default TagSelect;
